import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { Container, Text, Sprite, Graphics } from "@pixi/react";
import * as PIXI from "pixi.js";
import { TextStyle, TextMetrics } from "pixi.js";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import anime from "animejs";
import { createButtonPressAnimation } from "../../../../utilities";
import {
  closeErrorPopup,
  setUserInterfaceDisabled,
  setSpinComplete,
  setErrorRecoveryTransitionActive,
  setReelsFrozen,
  resetPotCollection,
  clearPotCollectionWin,
  clearFeatureStateToBase,
  setFeatureSpinActive,
  setFeatureSpinComplete,
  setCriticalAnimationPlaying,
  setResetMainGameVisualEffects,
} from "../../../../State/actions";
import {
  selectErrorPopupActive,
  selectErrorPopupCode,
  selectReelsFrozen,
} from "../../../../State/selectors";
import { refreshGameData } from "../../../../State/Slices/gameSettingsSlice";
import store from "../../../../State/Store/store";
import StorageManager from "../../../../utils/StorageManager";
import TickerTimeout from "../../../../utils/TickerTimeout";
import {
  useDimensions,
  ASPECT_RATIO_BREAKPOINTS,
} from "../../../../Contexts/DimensionsContext";
import { getNewUITexture } from "../../../../assets";
import { FONT_CONFIG, TEXT_STYLE_CONFIG } from "../../../../pixiSettings";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";

// Development mode - set to true to test popup with long message
const DEV_MODE = false;

// Overlay settings
const OVERLAY_BACKGROUND_ALPHA = 0.6;
const OVERLAY_BACKGROUND_COLOR = 0x000000;
const OVERLAY_FADE_DURATION = 300;

// Popup animation settings (same as InfoPopup)
const POPUP_SCALE_DURATION = 400;
const POPUP_SCALE_EASING = "easeOutBack";
const POPUP_SCALE_DELAY = 150;

// Popup panel settings
const PANEL_BACKGROUND_COLOR = 0x000000;
const PANEL_BACKGROUND_ALPHA = 0.8;

// ── Button reference size ────────────────────────────────────────────────────
// Fixed reference dimensions for popup buttons. These define the LAYOUT size
// independent of the actual texture resolution. When the spritesheet is
// re-exported at a different DPI the texture pixels change but the intended
// on-screen size stays the same.
const BUTTON_REF_WIDTH = 160;
const BUTTON_REF_HEIGHT = 64;

// ── Fixed panel reference dimensions ─────────────────────────────────────────
// Measured at desktop scale (1.175x). Panel size is constant across all content;
// text auto-shrinks to fit. Shared with InfoPopup.
const PANEL_REF_WIDTH = 504;
const PANEL_REF_HEIGHT = 342;
const DESKTOP_SCALE_REF = 1.175;

// Error popup configuration - Unified scaling system (like BuyBonus)
const ERROR_POPUP_CONFIG = {
  // Reference width for base values (all base values are defined at this width)
  REFERENCE_WIDTH: 600,

  // Base values at reference width (600px)
  basePaddingH: 22, // Side padding (horizontal)
  basePaddingV: 42, // Top/bottom padding (vertical)
  baseMessageFontSize: 20,
  baseButtonFontSize: 24,
  baseTextButtonGap: 55,
  baseBorderRadius: 20,
  baseMinEdgePadding: 20, // Minimum gap from screen edges
  baseContentWidth: 370, // Narrow content for tall proportions

  // Device-specific scale multipliers (ONLY scale, everything else follows)
  DEVICE_CONFIGS: {
    MOBILE_SMALL: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        scaleMultiplier: 0.575,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        scaleMultiplier: 0.65,
      },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        scaleMultiplier: 0.79,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        scaleMultiplier: 0.7125,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        scaleMultiplier: 0.9,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        scaleMultiplier: 0.7,
      },
    },
    SMALL_DESKTOP: {
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        scaleMultiplier: 0.725,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        scaleMultiplier: 0.97,
      },
    },
    WIDE_DESKTOP: {
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        scaleMultiplier: 1.14,
      },
    },
  },
};

const WarningPopup = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { dimensions, device, orientation, isMobileSmall } = useDimensions();
  const errorPopupActive = useSelector(selectErrorPopupActive);
  const errorCode = useSelector(selectErrorPopupCode);
  const reelsFrozen = useSelector(selectReelsFrozen);

  // Extract deposit_url from window options
  const depositUrl = window.__OPTIONS__?.deposit_url;

  const isOpen = DEV_MODE ? true : errorPopupActive; // Force open in dev mode

  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const popupContentRef = useRef(null);

  // Refs for button sprites and animation state
  const okButtonSpriteRef = useRef(null);
  const depositButtonSpriteRef = useRef(null);
  const isOkPressAnimatingRef = useRef(false);
  const isDepositPressAnimatingRef = useRef(false);

  // Create brightness filters for hover effects
  const okBrightnessFilter = useRef(new PIXI.ColorMatrixFilter());
  okBrightnessFilter.current.brightness(2.0, false);

  const depositBrightnessFilter = useRef(new PIXI.ColorMatrixFilter());
  depositBrightnessFilter.current.brightness(2.0, false);

  // Use refs for hover state to avoid unnecessary re-renders
  const isOkHoveredRef = useRef(false);
  const isDepositHoveredRef = useRef(false);

  // Use error code directly as translation key
  // For error 301: use "301nodep" if no deposit_url, otherwise use "301"
  // For errors without specific translations (600, 700, etc): use "default" with error code
  const translationKey = useMemo(() => {
    if (errorCode === 301) {
      return depositUrl ? "301" : "301nodep";
    }
    return String(errorCode);
  }, [errorCode, depositUrl]);

  // Check if translation exists for this error code, otherwise use default
  const errorMessage = useMemo(() => {
    if (!errorCode) {
      return t("default", { code: "unknown" });
    }

    // Try to get the translation
    const translatedMessage = t(translationKey);

    // If translation key equals the returned message, it means no translation exists
    // (i18n returns the key itself when translation is missing)
    if (translatedMessage === translationKey) {
      // Use default message with error code injected
      return t("default", { code: errorCode });
    }

    return translatedMessage;
  }, [errorCode, translationKey, t]);

  // Memoize device config — sub-classify desktop by viewport width,
  // sub-classify mobile as MOBILE_SMALL for compact phones
  const deviceConfigMemo = useMemo(() => {
    if (device === PixiSettings.DEVICE.DESKTOP) {
      let desktopSize;
      if (
        dimensions.width <= ASPECT_RATIO_BREAKPOINTS.SMALL_DESKTOP_MAX_WIDTH
      ) {
        desktopSize = "SMALL_DESKTOP";
      } else if (
        dimensions.width >= ASPECT_RATIO_BREAKPOINTS.WIDE_DESKTOP_MIN_WIDTH
      ) {
        desktopSize = "WIDE_DESKTOP";
      } else {
        desktopSize = "DESKTOP";
      }
      const configs = ERROR_POPUP_CONFIG.DEVICE_CONFIGS;
      return (configs[desktopSize] || configs[PixiSettings.DEVICE.DESKTOP])[
        PixiSettings.ORIENTATION.LANDSCAPE
      ];
    }
    if (isMobileSmall) {
      return (
        ERROR_POPUP_CONFIG.DEVICE_CONFIGS.MOBILE_SMALL?.[orientation] ||
        ERROR_POPUP_CONFIG.DEVICE_CONFIGS[PixiSettings.DEVICE.MOBILE][
          orientation
        ]
      );
    }
    return (
      ERROR_POPUP_CONFIG.DEVICE_CONFIGS[device]?.[orientation] ||
      ERROR_POPUP_CONFIG.DEVICE_CONFIGS[PixiSettings.DEVICE.DESKTOP][
        PixiSettings.ORIENTATION.LANDSCAPE
      ]
    );
  }, [device, orientation, dimensions.width, isMobileSmall]);

  const centerX = useMemo(() => dimensions.width / 2, [dimensions.width]);
  const centerY = useMemo(() => dimensions.height / 2, [dimensions.height]);

  // Get button texture
  const buttonTexture = useMemo(
    () => getNewUITexture("bonus_card_bet_display_background"),
    [],
  );

  // Popup scale — device multiplier with viewport clamping against fixed panel size
  const popupScale = useMemo(() => {
    const deviceScale = deviceConfigMemo.scaleMultiplier || 1.0;
    const scaledW = (PANEL_REF_WIDTH / DESKTOP_SCALE_REF) * deviceScale;
    const scaledH = (PANEL_REF_HEIGHT / DESKTOP_SCALE_REF) * deviceScale;
    const maxWidth = dimensions.width * 0.85;
    const maxHeight = dimensions.height * 0.85;
    const widthRatio = scaledW > maxWidth ? maxWidth / scaledW : 1;
    const heightRatio = scaledH > maxHeight ? maxHeight / scaledH : 1;
    return deviceScale * Math.min(widthRatio, heightRatio);
  }, [deviceConfigMemo, dimensions.width, dimensions.height]);

  // ── Fixed panel dimensions ─────────────────────────────────────────────────
  const panelWidth = useMemo(
    () => (PANEL_REF_WIDTH / DESKTOP_SCALE_REF) * popupScale,
    [popupScale],
  );
  const panelHeight = useMemo(
    () => (PANEL_REF_HEIGHT / DESKTOP_SCALE_REF) * popupScale,
    [popupScale],
  );

  // Apply popup scale to base values
  const paddingH = useMemo(
    () => ERROR_POPUP_CONFIG.basePaddingH * popupScale,
    [popupScale],
  );
  const paddingV = useMemo(
    () => ERROR_POPUP_CONFIG.basePaddingV * popupScale,
    [popupScale],
  );
  const buttonFontSize = useMemo(
    () => Math.round(ERROR_POPUP_CONFIG.baseButtonFontSize * popupScale),
    [popupScale],
  );
  const borderRadius = useMemo(
    () => ERROR_POPUP_CONFIG.baseBorderRadius * popupScale,
    [popupScale],
  );
  const contentWidth = useMemo(
    () => ERROR_POPUP_CONFIG.baseContentWidth * popupScale,
    [popupScale],
  );
  const buttonWidth = useMemo(
    () => BUTTON_REF_WIDTH * popupScale,
    [popupScale],
  );
  const buttonHeight = useMemo(
    () => BUTTON_REF_HEIGHT * popupScale,
    [popupScale],
  );

  // ── Auto-sized font ────────────────────────────────────────────────────────
  // Panel is fixed; text shrinks to fit within available height.
  const textAreaTopMultiplier = 1.0; // Top padding for text area (below "!")
  const bottomPaddingMultiplier = 0.4; // Button distance from panel bottom edge
  const textToButtonGapMultiplier = 0.3; // Gap between text area and button
  // Extra lift for the button only — does NOT feed into the text-area height/
  // position calculations above, so the message text stays exactly as-is.
  const buttonExtraLiftMultiplier = 0.36;

  const messageFontSize = useMemo(() => {
    // Text area: from topPad down to gap above button
    const textAreaTop = paddingV * textAreaTopMultiplier;
    const textAreaBottom =
      paddingV * bottomPaddingMultiplier +
      buttonHeight +
      paddingV * textToButtonGapMultiplier;
    const availableTextHeight = panelHeight - textAreaTop - textAreaBottom;

    let fontSize = Math.round(
      ERROR_POPUP_CONFIG.baseMessageFontSize * popupScale,
    );

    // Measure text at ideal font size and shrink if it overflows
    const measure = (size) => {
      const style = new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: size,
        fontWeight: "700",
        fill: 0xffffff,
        align: "center",
        wordWrap: true,
        wordWrapWidth: contentWidth,
        breakWords: true,
        ...TEXT_STYLE_CONFIG.OUTLINE,
      });
      return TextMetrics.measureText(errorMessage, style).height;
    };

    const measuredHeight = measure(fontSize);
    if (measuredHeight > availableTextHeight && measuredHeight > 0) {
      const ratio = availableTextHeight / measuredHeight;
      fontSize = Math.max(10, Math.floor(fontSize * ratio));
    }

    return fontSize;
  }, [
    popupScale,
    panelHeight,
    paddingV,
    buttonHeight,
    contentWidth,
    errorMessage,
  ]);

  // Text styling - MUST be before early return
  const messageStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: messageFontSize,
        fontWeight: "700",
        fill: 0xffffff,
        align: "center",
        wordWrap: true,
        wordWrapWidth: contentWidth,
        breakWords: true,
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
    [messageFontSize, contentWidth],
  );

  // Button text styling - MUST be before early return
  const buttonTextStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: buttonFontSize,
        fontWeight: "700",
        fill: 0xffffff,
        align: "center",
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
    [buttonFontSize],
  );

  // Center the panel perfectly in the middle of the screen
  const panelX = useMemo(() => centerX - panelWidth / 2, [centerX, panelWidth]);
  const panelY = useMemo(
    () => centerY - panelHeight / 2,
    [centerY, panelHeight],
  );

  // Position content — center text vertically within the text area
  const messageY = useMemo(() => {
    const textAreaTop = panelY + paddingV * textAreaTopMultiplier;
    const textAreaBottom =
      panelY +
      panelHeight -
      paddingV * bottomPaddingMultiplier -
      buttonHeight -
      paddingV * textToButtonGapMultiplier;
    return (textAreaTop + textAreaBottom) / 2;
  }, [panelY, paddingV, panelHeight, buttonHeight]);
  const buttonY = useMemo(
    () =>
      panelY +
      panelHeight -
      paddingV * (bottomPaddingMultiplier + buttonExtraLiftMultiplier) -
      buttonHeight / 2,
    [panelY, panelHeight, paddingV, buttonHeight],
  );

  // Exclamation mark position (top left corner with padding offset)
  const exclamationX = useMemo(
    () => panelX + paddingH * 0.5,
    [panelX, paddingH],
  );
  const exclamationY = useMemo(
    () => panelY + paddingV * 0.15,
    [panelY, paddingV],
  );
  const exclamationFontSize = useMemo(
    () => Math.round(messageFontSize * 1.8),
    [messageFontSize],
  );

  // Memoize exclamation mark text style
  const exclamationStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: exclamationFontSize,
        fontWeight: "700",
        fill: 0xffffff,
        align: "center",
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
    [exclamationFontSize],
  );

  // Memoize translation texts
  const okText = useMemo(() => t("ok"), [t]);
  const depositText = useMemo(() => t("deposit"), [t]);

  // Memoize button positions for two-button layout
  const okButtonX = useMemo(
    () => centerX - buttonWidth / 2 - paddingH * 0.5,
    [centerX, buttonWidth, paddingH],
  );
  const depositButtonX = useMemo(
    () => centerX + buttonWidth / 2 + paddingH * 0.5,
    [centerX, buttonWidth, paddingH],
  );

  // Memoize Graphics draw functions
  const drawOverlay = useCallback(
    (g) => {
      g.clear();
      g.beginFill(OVERLAY_BACKGROUND_COLOR);
      g.drawRect(0, 0, dimensions.width, dimensions.height);
      g.endFill();
    },
    [dimensions.width, dimensions.height],
  );

  const drawPanel = useCallback(
    (g) => {
      g.clear();
      g.beginFill(PANEL_BACKGROUND_COLOR, PANEL_BACKGROUND_ALPHA);
      g.drawRoundedRect(panelX, panelY, panelWidth, panelHeight, borderRadius);
      g.endFill();
    },
    [panelX, panelY, panelWidth, panelHeight, borderRadius],
  );

  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
  }, [isClosing]);

  // Hover effect handlers for OK button
  const handleOkPointerOver = useCallback(() => {
    if (okButtonSpriteRef.current) {
      isOkHoveredRef.current = true;
      if (
        !okButtonSpriteRef.current.filters ||
        okButtonSpriteRef.current.filters.length === 0
      ) {
        okButtonSpriteRef.current.filters = [okBrightnessFilter.current];
      }
    }
  }, []);

  const handleOkPointerOut = useCallback(() => {
    if (okButtonSpriteRef.current) {
      isOkHoveredRef.current = false;
      okButtonSpriteRef.current.filters = [];
    }
  }, []);

  // Hover effect handlers for Deposit button
  const handleDepositPointerOver = useCallback(() => {
    if (depositButtonSpriteRef.current) {
      isDepositHoveredRef.current = true;
      if (
        !depositButtonSpriteRef.current.filters ||
        depositButtonSpriteRef.current.filters.length === 0
      ) {
        depositButtonSpriteRef.current.filters = [
          depositBrightnessFilter.current,
        ];
      }
    }
  }, []);

  const handleDepositPointerOut = useCallback(() => {
    if (depositButtonSpriteRef.current) {
      isDepositHoveredRef.current = false;
      depositButtonSpriteRef.current.filters = [];
    }
  }, []);

  // OK button click handler with press animation
  const handleOkClick = useCallback(
    (e) => {
      e.stopPropagation();

      // Play click sound
      soundManager.play(SOUND_NAME.button_click);

      // Add press animation before handling click
      if (okButtonSpriteRef.current && !isOkPressAnimatingRef.current) {
        isOkPressAnimatingRef.current = true;
        createButtonPressAnimation(okButtonSpriteRef.current, {
          targetWidth: buttonWidth,
          targetHeight: buttonHeight,
          onComplete: () => {
            isOkPressAnimatingRef.current = false;
            // Close popup after animation completes
            handleClose();
          },
        });
      } else {
        // If animation is already running or sprite not ready, close immediately
        handleClose();
      }
    },
    [buttonWidth, buttonHeight, handleClose],
  );

  // Deposit button click handler with press animation
  const handleDepositClick = useCallback(
    (e) => {
      e.stopPropagation();

      // Play click sound
      soundManager.play(SOUND_NAME.button_click);

      // Add press animation before handling click
      if (
        depositButtonSpriteRef.current &&
        !isDepositPressAnimatingRef.current
      ) {
        isDepositPressAnimatingRef.current = true;
        createButtonPressAnimation(depositButtonSpriteRef.current, {
          targetWidth: buttonWidth,
          targetHeight: buttonHeight,
          onComplete: () => {
            isDepositPressAnimatingRef.current = false;
            // Redirect after animation completes (top-level window for iframe support)
            if (depositUrl) {
              try {
                window.top.location.href = depositUrl;
              } catch (e) {
                window.open(depositUrl, "_top");
              }
            }
          },
        });
      } else {
        // If animation is already running or sprite not ready, redirect immediately
        if (depositUrl) {
          try {
            window.top.location.href = depositUrl;
          } catch (e) {
            window.open(depositUrl, "_top");
          }
        }
      }
    },
    [buttonWidth, buttonHeight, depositUrl],
  );

  // Handle overlay fade in, popup scale up, and UI disable when opening (same as InfoPopup)
  useEffect(() => {
    if (isOpen && !isClosing) {
      // Disable UI when popup opens
      dispatch(setUserInterfaceDisabled(true));

      // 1. Fade in overlay
      anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_BACKGROUND_ALPHA,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
      });

      // 2. Scale up popup (with slight delay, starts during overlay fade)
      if (popupContentRef.current) {
        anime({
          targets: popupContentRef.current.scale,
          x: [0, 1],
          y: [0, 1],
          duration: POPUP_SCALE_DURATION,
          delay: POPUP_SCALE_DELAY,
          easing: POPUP_SCALE_EASING,
        });
      }
    } else if (!isOpen && !isClosing) {
      // Re-enable UI when popup closed externally (no animation needed)
      dispatch(setUserInterfaceDisabled(false));
    }
  }, [
    isOpen,
    isClosing,
    dispatch,
    panelWidth,
    panelHeight,
    popupScale,
    contentWidth,
    dimensions.width,
    dimensions.height,
    device,
    orientation,
  ]);

  // Handle close animation when user clicks OK - same as InfoPopup (scale down popup, fade overlay)
  useEffect(() => {
    if (!isClosing) return;

    let overlayAnim = null;
    let popupAnim = null;

    // 1. Scale down popup (reverse of scale up)
    if (popupContentRef.current) {
      popupAnim = anime({
        targets: popupContentRef.current.scale,
        x: 0,
        y: 0,
        duration: POPUP_SCALE_DURATION,
        easing: "easeInBack",
      });
    }

    // 2. Fade out overlay (can run in parallel)
    overlayAnim = anime({
      targets: { alpha: OVERLAY_BACKGROUND_ALPHA },
      alpha: 0,
      duration: OVERLAY_FADE_DURATION,
      delay: 50,
      easing: "easeInQuad",
      update: (anim) => {
        setOverlayAlpha(anim.animations[0].currentValue);
      },
      complete: () => {
        setOverlayAlpha(0);
        dispatch(setUserInterfaceDisabled(false));
        dispatch(closeErrorPopup());
        dispatch(setSpinComplete(true));

        // ONLY trigger error recovery if reels were actually frozen.
        // All cleanup (bonus reset, refreshGameData) is deferred to
        // ErrorRecoveryOverlay Phase 2 — behind the fully-black screen.
        if (reelsFrozen) {
          dispatch(setErrorRecoveryTransitionActive(true));
          dispatch(setReelsFrozen(false));
        } else {
          // No overlay — run cleanup inline (non-frozen errors like 205 base game)
          const isFreeRoundsActive =
            store.getState().freeRounds?.status === "active";
          if (errorCode === 205 || errorCode === 206 || isFreeRoundsActive) {
            if (isFreeRoundsActive && errorCode !== 205) {
              StorageManager.clearFreeRoundsRound();
            }

            // Wipe local PC Redux; refresh rehydrates if server still in bonus.
            // Keep bonus LS until after refresh so mid-bonus flipped positions survive.
            const potCollectionState = store.getState().potCollection;
            const wasPotCollection =
              potCollectionState?.potCollectionActive ||
              potCollectionState?.potCollectionTriggered;
            if (wasPotCollection) {
              dispatch(resetPotCollection());
              dispatch(clearPotCollectionWin());
              dispatch(clearFeatureStateToBase());
              dispatch(setFeatureSpinActive(false));
              dispatch(setFeatureSpinComplete(true));
              dispatch(setCriticalAnimationPlaying(false));
              dispatch(setResetMainGameVisualEffects(true));
            }

            // useApiData: true — same as ErrorRecoveryOverlay; hydrate from server
            dispatch(
              refreshGameData({
                useApiData: true,
                ignoreFreeRoundsStorage: true,
              }),
            ).then((action) => {
              const featureState = action?.payload?.result?.featureState;
              const isBonusNow = featureState?.type === "bonus";
              const liveBonusPhase =
                isBonusNow &&
                (featureState.phase === "intro" ||
                  featureState.phase === "in_progress");

              if (liveBonusPhase && featureState.phase === "intro") {
                dispatch(setResetMainGameVisualEffects(true));
                dispatch(setSpinComplete(false));
                new TickerTimeout(() => {
                  dispatch(setSpinComplete(true));
                }, 50);
              } else if (
                liveBonusPhase &&
                featureState.phase === "in_progress"
              ) {
                // Mid-bonus respins restore handled by existing reload path
              } else {
                // Server left bonus (other tab finished) — full base return
                StorageManager.clearBonusGameRound();
                StorageManager.clearTriggerWin();
                dispatch(resetPotCollection());
                dispatch(clearPotCollectionWin());
                dispatch(clearFeatureStateToBase());
                dispatch(setFeatureSpinActive(false));
                dispatch(setFeatureSpinComplete(true));
                dispatch(setCriticalAnimationPlaying(false));
                dispatch(setResetMainGameVisualEffects(true));
                dispatch(setSpinComplete(true));
              }
            });
          }
        }

        setIsClosing(false);
      },
    });

    return () => {
      if (overlayAnim) overlayAnim.pause();
      if (popupAnim) popupAnim.pause();
    };
  }, [isClosing, dispatch, reelsFrozen, errorCode]);

  // Clean up hover effects on unmount
  useEffect(() => {
    const okSprite = okButtonSpriteRef.current;
    const depositSprite = depositButtonSpriteRef.current;
    return () => {
      if (okSprite) {
        okSprite.filters = [];
      }
      if (depositSprite) {
        depositSprite.filters = [];
      }
    };
  }, []);

  // Early return AFTER all hooks - stay mounted during close animation
  if (!isOpen && !isClosing) {
    return null;
  }

  return (
    <Container name="warning_popup" zIndex={10000} sortableChildren={true}>
      {/* Black overlay - NOT clickable, more transparent */}
      <Graphics
        draw={drawOverlay}
        alpha={overlayAlpha}
        eventMode="static"
        pointerdown={(e) => {
          e.stopPropagation();
          // Do nothing - user cannot close by clicking overlay
        }}
        zIndex={10000}
      />

      {/* Content container - animated scale (same structure as InfoPopup) */}
      <Container
        name="popup_content"
        ref={popupContentRef}
        zIndex={10001}
        x={centerX}
        y={centerY}
        pivot={{ x: centerX, y: centerY }}
      >
        {/* Dark panel background - wraps text and button */}
        <Graphics draw={drawPanel} />

        {/* Exclamation mark - top left corner */}
        <Text
          text="!"
          style={exclamationStyle}
          anchor={{ x: 0, y: 0 }}
          x={exclamationX}
          y={exclamationY}
          resolution={2}
        />

        {/* Error message - dynamically positioned based on text height */}
        <Text
          text={errorMessage}
          style={messageStyle}
          anchor={0.5}
          x={centerX}
          y={messageY}
          resolution={2}
        />

        {/* Buttons - single OK button or two buttons for error 301 with deposit */}
        {errorCode === 301 && depositUrl ? (
          // Two buttons side by side for error 301 with deposit URL
          <>
            {/* OK Button - left side */}
            <Container name="ok_button" x={okButtonX} y={buttonY}>
              {/* Interactive area - handles all pointer events */}
              <Graphics
                name="ok_button_interactive_area"
                eventMode="static"
                cursor="pointer"
                hitArea={
                  new PIXI.Rectangle(
                    -buttonWidth / 2,
                    -buttonHeight / 2,
                    buttonWidth,
                    buttonHeight,
                  )
                }
                onpointertap={handleOkClick}
                onpointerover={handleOkPointerOver}
                onpointerout={handleOkPointerOut}
                draw={(g) => {
                  g.clear();
                  // Nearly invisible fill (required for hit detection to work)
                  g.beginFill(0x000000, 0.01);
                  g.drawRect(
                    -buttonWidth / 2,
                    -buttonHeight / 2,
                    buttonWidth,
                    buttonHeight,
                  );
                  g.endFill();
                }}
              />

              <Sprite
                ref={okButtonSpriteRef}
                texture={buttonTexture}
                anchor={0.5}
                width={buttonWidth}
                height={buttonHeight}
                eventMode="none"
              />
              <Text
                text={okText}
                style={buttonTextStyle}
                anchor={0.5}
                resolution={2}
                eventMode="none"
              />
            </Container>

            {/* Deposit Button - right side */}
            <Container name="deposit_button" x={depositButtonX} y={buttonY}>
              {/* Interactive area - handles all pointer events */}
              <Graphics
                name="deposit_button_interactive_area"
                eventMode="static"
                cursor="pointer"
                hitArea={
                  new PIXI.Rectangle(
                    -buttonWidth / 2,
                    -buttonHeight / 2,
                    buttonWidth,
                    buttonHeight,
                  )
                }
                onpointertap={handleDepositClick}
                onpointerover={handleDepositPointerOver}
                onpointerout={handleDepositPointerOut}
                draw={(g) => {
                  g.clear();
                  // Nearly invisible fill (required for hit detection to work)
                  g.beginFill(0x000000, 0.01);
                  g.drawRect(
                    -buttonWidth / 2,
                    -buttonHeight / 2,
                    buttonWidth,
                    buttonHeight,
                  );
                  g.endFill();
                }}
              />

              <Sprite
                ref={depositButtonSpriteRef}
                texture={buttonTexture}
                anchor={0.5}
                width={buttonWidth}
                height={buttonHeight}
                eventMode="none"
              />
              <Text
                text={depositText}
                style={buttonTextStyle}
                anchor={0.5}
                resolution={2}
                eventMode="none"
              />
            </Container>
          </>
        ) : (
          // Single OK button for all other cases
          <Container name="ok_button" x={centerX} y={buttonY}>
            {/* Interactive area - handles all pointer events */}
            <Graphics
              name="ok_button_interactive_area"
              eventMode="static"
              cursor="pointer"
              hitArea={
                new PIXI.Rectangle(
                  -buttonWidth / 2,
                  -buttonHeight / 2,
                  buttonWidth,
                  buttonHeight,
                )
              }
              onpointertap={handleOkClick}
              onpointerover={handleOkPointerOver}
              onpointerout={handleOkPointerOut}
              draw={(g) => {
                g.clear();
                // Nearly invisible fill (required for hit detection to work)
                g.beginFill(0x000000, 0.01);
                g.drawRect(
                  -buttonWidth / 2,
                  -buttonHeight / 2,
                  buttonWidth,
                  buttonHeight,
                );
                g.endFill();
              }}
            />

            <Sprite
              ref={okButtonSpriteRef}
              texture={buttonTexture}
              anchor={0.5}
              width={buttonWidth}
              height={buttonHeight}
              eventMode="none"
            />
            <Text
              text={okText}
              style={buttonTextStyle}
              anchor={0.5}
              resolution={2}
              eventMode="none"
            />
          </Container>
        )}
      </Container>
    </Container>
  );
};

export default WarningPopup;
